// client/src/components/pages/LexiStar/FleetTargets/targetsLogic.ts (ST-00248)
//
// What the Targets section and the actual-vs-target visuals share, pure: a target's words (the
// value with its unit and period, what it applies to, its status), the add / change form's
// draft and checks, the outcome words, and the points a series' chart draws. The arithmetic
// (a weekly target spread over days, rates held, "to date") is the server's
// (performance/targets.ts); the page shows what GET /targets/progress returns.

import type {
  PerformanceTarget,
  TargetGrain,
  TargetInput,
  TargetMetric,
  TargetMetricKey,
  TargetOutcome,
  TargetPoint,
  TargetScopeType,
  TargetSeries,
  TargetStatus,
  FleetGrain,
} from '../../../../types/lexistar-performance';
import { bucketLabel, bucketTick } from './fleetLogic';

// ── Words ───────────────────────────────────────────────────────────────────────────────

const group = (n: number, digits: number): string =>
  n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: digits }).replace(/^-/, '−');

/** Decimals a unit is shown with. */
const DIGITS: Record<string, number> = { count: 1, t: 1, km: 0, 'L/100km': 1, 'L/t': 2, '%': 1 };

/** A value with its unit: "12 trips", "1,234.5 t", "41.2 L/100 km", "1.85 L/t", "90%". */
export function fmtMetricValue(unit: string, value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  const digits = DIGITS[unit] ?? 1;
  const num = group(n, Math.abs(n) >= 1000 ? 0 : digits);
  switch (unit) {
    case 'count':
      return `${num} trip${Math.abs(n) === 1 ? '' : 's'}`;
    case '%':
      return `${num}%`;
    case 'L/100km':
      return `${num} L/100 km`;
    default:
      return `${num} ${unit}`;
  }
}

/** A signed variance: "+5 trips", "−2.5 L/100 km", "+1.2 pts" for a percentage. */
export function fmtVariance(unit: string, value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  const sign = n > 0 ? '+' : n < 0 ? '−' : '±';
  if (unit === '%') return `${sign}${group(Math.abs(n), 1)} pts`;
  return `${sign}${fmtMetricValue(unit, Math.abs(n))}`;
}

export const GRAIN_WORD: Record<TargetGrain, string> = { day: 'a day', week: 'a week', month: 'a month' };
export const GRAIN_OPTIONS: Array<{ value: TargetGrain; label: string }> = [
  { value: 'day', label: 'Per day' },
  { value: 'week', label: 'Per week' },
  { value: 'month', label: 'Per month' },
];

/** "1,200 t a week", "90%" (a rate holds every day). */
export function targetText(m: Pick<TargetMetric, 'unit' | 'kind'> | undefined, t: Pick<PerformanceTarget, 'value' | 'grain'>): string {
  const unit = m ? m.unit : '';
  const value = fmtMetricValue(unit, t.value);
  return m && m.kind === 'amount' ? `${value} ${GRAIN_WORD[t.grain]}` : value;
}

export const SCOPE_WORD: Record<TargetScopeType, string> = { tenant: 'Whole organisation', route: 'Route', asset: 'Truck' };

/** What a target applies to: "Whole organisation", "Route RT-01 · Mine to siding", "Truck ABC 123 GP". */
export function scopeText(t: { scope_type: TargetScopeType; scope_label: string | null; scope_id: string }): string {
  if (t.scope_type === 'tenant') return SCOPE_WORD.tenant;
  return `${SCOPE_WORD[t.scope_type]} ${t.scope_label || t.scope_id}`;
}

export const STATUS_META: Record<TargetStatus, { label: string; pill: string }> = {
  in_force: { label: 'In force', pill: 'bg-primary-50 text-primary-800 ring-1 ring-inset ring-primary-200' },
  upcoming: { label: 'Starts later', pill: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200' },
  ended: { label: 'Ended', pill: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200' },
  retired: { label: 'Retired', pill: 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200 line-through' },
};

/** Never colour alone: each outcome has a glyph and a word. */
export const OUTCOME_META: Record<TargetOutcome, { label: string; glyph: string; pill: string }> = {
  met: { label: 'On target', glyph: '✓', pill: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200' },
  missed: { label: 'Off target', glyph: '✗', pill: 'bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200' },
  no_target: { label: 'No target', glyph: '–', pill: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200' },
  no_data: { label: 'No figure yet', glyph: '…', pill: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200' },
};

/** "Higher is better" / "Lower is better". */
export const directionText = (m: Pick<TargetMetric, 'direction'>): string =>
  m.direction === 'lower' ? 'Lower is better' : 'Higher is better';

// ── The form ────────────────────────────────────────────────────────────────────────────

export interface TargetDraft {
  metric: TargetMetricKey | '';
  scope_type: TargetScopeType;
  scope_id: string;
  grain: TargetGrain;
  value: string;
  effective_from: string;
  effective_to: string;
  notes: string;
}

export const emptyDraft = (today: string): TargetDraft => ({
  metric: '',
  scope_type: 'tenant',
  scope_id: '',
  grain: 'month',
  value: '',
  effective_from: `${today.slice(0, 7)}-01`,
  effective_to: '',
  notes: '',
});

/** The draft's problems, by field (empty = it can be sent). */
export function draftProblems(d: TargetDraft, metrics: readonly TargetMetric[]): Partial<Record<keyof TargetDraft, string>> {
  const out: Partial<Record<keyof TargetDraft, string>> = {};
  const m = metrics.find((x) => x.key === d.metric);
  if (!m) out.metric = 'Choose what the target is for';
  if (m && !m.scopes.includes(d.scope_type)) out.scope_type = `A ${m.label.toLowerCase()} target cannot be set per ${SCOPE_WORD[d.scope_type].toLowerCase()}`;
  if (d.scope_type !== 'tenant' && !d.scope_id) out.scope_id = d.scope_type === 'route' ? 'Choose the route' : 'Choose the truck';
  const n = Number(d.value);
  if (d.value.trim() === '' || !Number.isFinite(n)) out.value = 'Enter the target as a number';
  else if (n < 0) out.value = 'A target cannot be negative';
  else if (m && m.unit === '%' && n > 100) out.value = 'A percentage is at most 100';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.effective_from)) out.effective_from = 'Choose the first day';
  if (d.effective_to && d.effective_to < d.effective_from) out.effective_to = 'The last day must not be before the first';
  if (d.notes.length > 500) out.notes = 'At most 500 characters';
  return out;
}

/** The API body of a valid draft. */
export function inputOf(d: TargetDraft, metrics: readonly TargetMetric[]): TargetInput {
  const m = metrics.find((x) => x.key === d.metric);
  return {
    metric: d.metric as TargetMetricKey,
    scope_type: d.scope_type,
    ...(d.scope_type === 'tenant' ? {} : { scope_id: d.scope_id }),
    ...(m && m.kind === 'amount' ? { grain: d.grain } : {}),
    value: Number(d.value),
    effective_from: d.effective_from,
    effective_to: d.effective_to || null,
    notes: d.notes.trim() || null,
  };
}

/** A change from `from`: a new version, or — from the target's first day or before — a correction. */
export const isCorrection = (t: Pick<PerformanceTarget, 'effective_from'>, from: string): boolean => from <= t.effective_from;

/** The scope types a metric allows, as options. */
export const scopeOptions = (m: Pick<TargetMetric, 'scopes'> | undefined): Array<{ value: TargetScopeType; label: string }> =>
  (m ? m.scopes : (['tenant', 'route', 'asset'] as TargetScopeType[])).map((s) => ({
    value: s,
    label: s === 'tenant' ? 'The whole organisation' : s === 'route' ? 'One route' : 'One truck',
  }));

/** The list's order: by metric (catalogue order), what it applies to, then the first day (latest first). */
export function sortTargets(targets: PerformanceTarget[], metrics: readonly TargetMetric[]): PerformanceTarget[] {
  const order = new Map(metrics.map((m, i) => [m.key, i]));
  const scope: Record<TargetScopeType, number> = { tenant: 0, route: 1, asset: 2 };
  return [...targets].sort(
    (a, b) =>
      (order.get(a.metric) ?? 99) - (order.get(b.metric) ?? 99) ||
      scope[a.scope_type] - scope[b.scope_type] ||
      String(a.scope_label ?? a.scope_id).localeCompare(String(b.scope_label ?? b.scope_id)) ||
      b.effective_from.localeCompare(a.effective_from)
  );
}

// ── The chart ───────────────────────────────────────────────────────────────────────────

export interface ProgressPoint {
  start: string;
  tick: string;
  label: string;
  actual: number | null;
  /** The target the actual is judged against (to date in a running period). */
  target: number | null;
  /** The chart's target line: the finished periods only (a running period's target so far would read as a drop). */
  targetLine: number | null;
  /** The running period's target so far (a lone hollow mark on the chart). */
  targetSoFar: number | null;
  /** The whole period's target. */
  fullTarget: number | null;
  variance: number | null;
  variancePct: number | null;
  outcome: TargetOutcome;
  partial: boolean;
}

export function progressPoints(s: TargetSeries, grain: FleetGrain): ProgressPoint[] {
  return s.points.map((p: TargetPoint) => ({
    start: p.start,
    tick: bucketTick(p.start, grain),
    label: bucketLabel({ start: p.start, partial: p.partial }, grain),
    actual: p.actual,
    target: p.target_to_date,
    targetLine: p.partial ? null : p.target_to_date,
    targetSoFar: p.partial ? p.target_to_date : null,
    fullTarget: p.target,
    variance: p.variance,
    variancePct: p.variance_pct,
    outcome: p.outcome,
    partial: p.partial,
  }));
}

/** How many periods met the target, of those that had one and a figure. */
export function periodsMet(s: TargetSeries): { met: number; judged: number } {
  const judged = s.points.filter((p) => p.outcome === 'met' || p.outcome === 'missed');
  return { met: judged.filter((p) => p.outcome === 'met').length, judged: judged.length };
}

/** The series in words: "Tonnes moved, whole organisation: 1,234 t against 1,500 t (−17.7%), off target; 2 of 4 weeks on target." */
export function seriesSummary(s: TargetSeries, grain: FleetGrain): string {
  const t = s.totals;
  const pct = t.variance_pct === null ? '' : ` (${t.variance_pct > 0 ? '+' : t.variance_pct < 0 ? '−' : '±'}${Math.abs(t.variance_pct).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%)`;
  const { met, judged } = periodsMet(s);
  const word = grain === 'day' ? 'day' : grain === 'week' ? 'week' : 'month';
  const periods = judged ? `; ${met} of ${judged} ${word}${judged === 1 ? '' : 's'} on target` : '';
  const scope = s.scope_type === 'tenant' ? 'whole organisation' : scopeText(s);
  return `${s.label}, ${scope}: ${fmtMetricValue(s.unit, t.actual)} against ${fmtMetricValue(s.unit, t.target_to_date)}${pct}, ${OUTCOME_META[t.outcome].label.toLowerCase()}${periods}.`;
}
